import React, { useEffect } from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Input } from '@lightspeed/flame/Input';
import { Text } from '@lightspeed/flame/Text';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ModuleCompareActionTypes,
  useModuleCompare,
} from 'root/apps/ModuleCompareUI/context/Context';
import { CompareDataApiResponse } from 'root/apps/ModuleCompareUI/ModuleCompare.types';
import { DangerAlert } from 'root/apps/MultiLocationSaleModal/DangerAlert';

const useFetchCompareAccountModuleList = (
  accountId: string,
  targetAccountId: string,
  enabled: boolean
) => {
  const { dispatch } = useModuleCompare();

  const query = useQuery({
    queryKey: [`${accountId}-module-diff-${targetAccountId}`],
    queryFn: () =>
      axios
        .get<CompareDataApiResponse>(
          `/staff/account/${accountId}/module/?compareAccountID=${targetAccountId}`
        )
        .then((response) => {
          return response.data.data.moduleList;
        }),
    enabled,
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: ModuleCompareActionTypes.SET_COMPARED_MODULES,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return {
    isFetching: query.isFetching,
    isError: query.isError,
  };
};

export const CompareByID: React.FC = () => {
  const {
    accountId,
    state: { activeMode },
    dispatch,
  } = useModuleCompare();

  const [secondAccountId, setSecondAccountId] = React.useState('');
  const [lastFetchedAccountId, setLastFetchedAccountId] = React.useState('');
  const { isFetching, isError } = useFetchCompareAccountModuleList(
    accountId,
    lastFetchedAccountId,
    !!lastFetchedAccountId
  );

  useEffect(() => {
    if (activeMode !== 'byId') {
      setSecondAccountId('');
      setLastFetchedAccountId('');
    }
  }, [activeMode]);

  const handleCompareClick = () => {
    if (secondAccountId) {
      dispatch({
        type: ModuleCompareActionTypes.SET_SECOND_ACCOUNT_ID,
        payload: secondAccountId,
      });
      dispatch({
        type: ModuleCompareActionTypes.SET_ACTIVE_MODE,
        payload: 'byId',
      });
      setLastFetchedAccountId(secondAccountId);
    }
  };

  return (
    <>
      {!isFetching && isError && (
        <DangerAlert
          title="Something went wrong"
          message={`Account ${lastFetchedAccountId} does not exist or there was an error fetching the data. Try again.`}
        />
      )}
      <Text fontSize={18} fontWeight={600} pb={2}>
        Current Modules in Current Environment
      </Text>
      <Text fontSize={16} fontWeight={600}>
        Current Account ID: {accountId}
      </Text>
      <Flex flexDirection="row" style={{ gap: '0.5rem' }} alignItems="center" mt={2}>
        <Text fontSize={16} fontWeight={600}>
          Enter the account ID to compare:
        </Text>
        <Box maxWidth="120px">
          <Input
            value={secondAccountId}
            type="number"
            onChange={(e) => {
              setSecondAccountId(e.target.value.trim());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCompareClick();
              }
            }}
          />
        </Box>
      </Flex>
      <Flex my={2} flexDirection="row" style={{ gap: '1rem' }} alignItems="center">
        <Button
          onClick={handleCompareClick}
          fill
          variant="secondary"
          loading={isFetching}
          width="300px"
        >
          Compare
        </Button>
        {!!secondAccountId !== false && secondAccountId !== lastFetchedAccountId ? (
          <Text size="small" color="gray-300">
            Click on compare to see the module difference
          </Text>
        ) : null}
      </Flex>
    </>
  );
};
